import { Mail, StickyNote, CalendarCheck, CheckCircle2, MousePointerClick } from 'lucide-react';
import type { TimelineEvent as Event } from '../hooks/useCommunicationTimeline.js';

const CATEGORY_LABEL: Record<string, string> = {
  moodle_welcome: 'Accès Moodle',
  qualiopi_form: 'Qualiopi — formulaire',
  qualiopi_relance: 'Qualiopi — relance',
  attestation: 'Attestation',
  manual: 'Email manuel',
  validate_receipt: 'Récap validation',
  other: 'Email',
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

interface Props {
  event: Event;
  showApprenantLink?: boolean;
}

/**
 * Composant générique — affiche un événement de la timeline communication.
 * Switch sur kind. Discriminé par TS.
 */
export function TimelineEventRow({ event, showApprenantLink }: Props) {
  if (event.kind === 'email' && event.email) {
    const e = event.email;
    return (
      <div className="flex gap-3 rounded-lg border border-sky-100 bg-sky-50/40 p-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
          <Mail className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-700">
              {CATEGORY_LABEL[e.category] ?? e.category}
            </span>
            <span className="text-[10px] text-neutral-500">{formatDate(e.sentAt)}</span>
            {e.openedAt && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Ouvert
              </span>
            )}
            {e.clickedAt && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-violet-700">
                <MousePointerClick className="h-2.5 w-2.5" />
                Cliqué
              </span>
            )}
            {e.status === 'bounced' && (
              <span className="rounded bg-rose-100 px-1 py-0 text-[10px] font-semibold text-rose-700">
                Bounce
              </span>
            )}
          </div>
          <div className="truncate text-xs font-semibold text-neutral-900">{e.subject}</div>
          <div className="truncate text-[10px] text-neutral-500">
            De {e.fromEmail} → {e.toEmail}
          </div>
          {showApprenantLink && event.apprenantId && (
            <a
              href={`/apprenants/${event.apprenantId}`}
              className="text-primary-600 mt-1 inline-block text-[10px] font-semibold hover:underline"
            >
              Voir fiche apprenant →
            </a>
          )}
        </div>
      </div>
    );
  }

  if (event.kind === 'note' && event.note) {
    const n = event.note;
    return (
      <div className="flex gap-3 rounded-lg border border-amber-100 bg-amber-50/40 p-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <StickyNote className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-neutral-900">
              {n.author.name ?? n.author.email.split('@')[0]}
            </span>
            <span className="text-[10px] text-neutral-500">{formatDate(n.createdAt)}</span>
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">
              Note · {n.category}
            </span>
          </div>
          <div className="whitespace-pre-wrap text-xs text-neutral-700">{n.content}</div>
          {showApprenantLink && (
            <a
              href={`/apprenants/${n.apprenantId}`}
              className="text-primary-600 mt-1 inline-block text-[10px] font-semibold hover:underline"
            >
              Voir fiche apprenant →
            </a>
          )}
        </div>
      </div>
    );
  }

  if (event.kind === 'rdv') {
    return (
      <div className="flex gap-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <CalendarCheck className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1 text-xs italic text-neutral-500">
          RDV — sync Zoho Bookings (à brancher en vague 3)
        </div>
      </div>
    );
  }

  return null;
}
